import { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { getOrganizationQp, Provider, useAuthUser as useFronteggAuthUser } from '@common/libs/auth';
import AuthContext from './authContext';
import { ADMIN_PORTAL_QP, AppType, AuthData, ENTER_MFA_PARAM, User } from './interfaces.ts';
import { Loader } from '@common/ui/Loader';
import {
  AdminPortal,
  FronteggThemeOptions,
  useAuthActions,
  useIsSteppedUp,
  useStepUp,
  useTenantsState,
} from '@frontegg/react';
import { useSearchParams } from 'react-router-dom';

import { getLoginPartialPath, useAuth } from '@common/libs/auth';
import { useNavigate } from 'react-router-dom';
import { useSessionStorage } from 'usehooks-ts';

export const AUTH_LS_KEY = '_REDIRECT_AFTER_LOGIN_';

const useCheckAuthentication = () => {
  const [ready, setReady] = useState(false);

  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const loginPath = getLoginPartialPath();
  const [redirectUrlAfterLogin, setRedirectUrlAfterLogin] = useSessionStorage<string | null>(AUTH_LS_KEY, null);

  useEffect(() => {
    const isOnLoginPage = window.location.pathname.includes(loginPath);

    if (isAuthenticated) {
      if (redirectUrlAfterLogin && redirectUrlAfterLogin !== window.location.pathname) {
        navigate(redirectUrlAfterLogin);
        setRedirectUrlAfterLogin(null);
      }
      setReady(true);
      return;
    }

    if (!isOnLoginPage) {
      setRedirectUrlAfterLogin(window.location.pathname + window.location.search);
      navigate(loginPath);
    }

    setReady(true);
  }, [isAuthenticated, loginPath, navigate, redirectUrlAfterLogin, setRedirectUrlAfterLogin]);

  return useMemo(() => isAuthenticated && !!user && !!user.accessToken && ready, [isAuthenticated, ready, user]);
};

function AponoAuthProvider({ children }: PropsWithChildren) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [authData, setAuthData] = useState<AuthData | null>(null);
  const fronteggUser = useFronteggAuthUser();
  const isAuthenticated = useCheckAuthentication();
  const { tenants } = useTenantsState();
  const { switchTenant } = useAuthActions();
  const stepUp = useStepUp();
  const isSteppedUp = useIsSteppedUp();

  useEffect(() => {
    if (isAuthenticated && fronteggUser) {
      const portalUser: User = {
        id: fronteggUser.id,
        email: fronteggUser.email,
        name: fronteggUser.name,
        roles: fronteggUser.roles,
        app: AppType.Portal,
        tenantId: fronteggUser.tenantId,
        profilePictureUrl: fronteggUser.profilePictureUrl || undefined,
        withUser: true,
        withLogo: true,
        isDarkSidebar: true,
      };

      setAuthData({
        user: portalUser,
        accessToken: fronteggUser.accessToken,
        tenants,
        isMfaPassed: isSteppedUp,
        isMfaEnrolled: fronteggUser.mfaEnrolled,
      });
    }
  }, [isAuthenticated, fronteggUser, tenants, switchTenant, isSteppedUp]);

  const showAdminPortal = useCallback((path?: string) => {
    if (path) window.location.hash = path;
    AdminPortal.show();
  }, []);

  const onSetUpMfa = () => {
    showAdminPortal('#/admin-box/privacy');
  };

  const onEnterMfa = () => {
    stepUp();
  };

  useEffect(() => {
    const adminPortalQp = searchParams.get(ADMIN_PORTAL_QP);
    if (adminPortalQp) {
      searchParams.delete(ADMIN_PORTAL_QP);
      setSearchParams(searchParams);
      showAdminPortal(adminPortalQp);
    }

    const enterMfaQP = searchParams.get(ENTER_MFA_PARAM);
    if (enterMfaQP) {
      searchParams.delete(ENTER_MFA_PARAM);
      setSearchParams(searchParams);
      stepUp();
    }
  }, [searchParams, setSearchParams, showAdminPortal, stepUp, authData]);

  if (!authData) {
    return <Loader />;
  }

  return (
    <AuthContext.Provider
      value={{
        ...authData,
        switchTenant,
        showAdminPortal,
        onSetUpMfa,
        onEnterMfa,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

function FronteggAuthProvider({ children }: PropsWithChildren) {
  const fronteggContext = {
    baseUrl: import.meta.env.VITE_FRONTEGG_URL || '',
    clientId: import.meta.env.VITE_FRONTEGG_CLIENT_ID || '',
    tenantResolver: () => ({ tenant: getOrganizationQp() }),
  };

  const themeOptions: FronteggThemeOptions = {
    adminPortal: {
      pages: {
        privacy: {
          fieldsProperties: {
            loginSessions: {
              appearance: 'hidden',
            },
          },
        },
      },
    },
  };

  const authOptions = {
    keepSessionAlive: true,
    enforceRedirectToSameSite: true,
    allowedRedirectOrigins: ['https://*.apono.io'],
  };

  return (
    <Provider
      contextOptions={fronteggContext}
      themeOptions={themeOptions}
      authOptions={authOptions}
      customLoader
      hostedLoginBox={false}
    >
      <AponoAuthProvider>{children}</AponoAuthProvider>
    </Provider>
  );
}

export default FronteggAuthProvider;
